import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditCard, Wallet, Bitcoin, Building2, Sparkles, Loader2 } from "lucide-react";
import DeckReveal from "@/components/DeckReveal";

const METHODS = [
  { id: "mbway", label: "MBWay", icon: Wallet },
  { id: "paypal", label: "PayPal", icon: CreditCard },
  { id: "transfer", label: "Transferência", icon: Building2 },
  { id: "crypto", label: "Crypto", icon: Bitcoin },
];

export default function Sorteio() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState("mbway");
  const [orders, setOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(r => setSettings(r.data));
  }, []);

  const loadOrders = async () => {
    try {
      const r = await api.get("/orders/mine");
      setOrders(r.data);
      // auto-pick: most recent paid order that hasn't been drawn — mas nunca
      // troca uma encomenda já activa a meio do reveal/spin (refreshUser()
      // dispara este load de novo a cada roleta girada; sem esta guarda,
      // uma encomenda antiga por revelar troca o utilizador de encomenda
      // sem aviso).
      setActiveOrder(current => {
        if (current) return current;
        return r.data.find(o => o.status === "paid" && !o.cards_drawn) || current;
      });
    } catch {}
  };

  useEffect(() => {
    if (user) loadOrders();
  }, [user]);

  const handleCheckout = async () => {
    if (!user) { login(); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/orders", { quantity: qty, payment_method: method });
      toast.success("Pedido criado! Aguarda confirmação do pagamento.");
      await loadOrders();
      setActiveOrder(null);
      navigate(`/pagamento/${r.data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a criar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-40 text-center text-gold font-display text-2xl">A carregar…</div>;

  // If there's an active paid+undrawn order, show deck reveal flow
  if (activeOrder) {
    return <DeckReveal order={activeOrder} onDone={() => { setActiveOrder(null); loadOrders(); }} />;
  }

  const total = (qty * (settings?.card_price_eur || 10)).toFixed(2);

  return (
    <main className="pt-28 pb-20 max-w-5xl mx-auto px-6 md:px-12">
      <motion.div initial={{opacity: 0, y: 16}} animate={{opacity: 1, y: 0}}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2"><Sparkles size={14}/> O Ritual</p>
        <h1 className="font-display text-5xl md:text-6xl mb-3">Adquire as tuas cartas</h1>
        <p className="text-[color:var(--text-2)] mb-12">Cada carta custa <span className="text-gold font-semibold">{settings?.card_price_eur ?? 10}€</span>. Compras quantas quiseres e depois reveles uma a uma do baralho virado para baixo.</p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="glass p-8">
          <h2 className="font-display text-3xl mb-6">1. Quantas cartas?</h2>
          <div className="flex items-center gap-4 mb-8">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="btn-secondary !px-4" data-testid="qty-decrease">−</button>
            <input type="number" min={1} max={33} value={qty}
              onChange={e => setQty(Math.max(1, Math.min(33, parseInt(e.target.value || "1"))))}
              className="input text-center text-3xl font-display w-24" data-testid="qty-input" />
            <button onClick={() => setQty(Math.min(33, qty + 1))} className="btn-secondary !px-4" data-testid="qty-increase">+</button>
            <div className="ml-auto text-right">
              <div className="text-[color:var(--text-3)] text-xs uppercase tracking-widest">Total</div>
              <div className="text-gold font-display text-4xl">{total}€</div>
            </div>
          </div>

          <h2 className="font-display text-3xl mb-4">2. Método de pagamento</h2>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {METHODS.map(m => (
              <button key={m.id} onClick={() => setMethod(m.id)}
                data-testid={`method-${m.id}`}
                className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${method===m.id ? "border-gold bg-[rgba(212,175,55,0.08)]" : "border-[color:var(--border)] hover:border-gold/40"}`}>
                <m.icon size={20} className="text-gold"/> <span>{m.label}</span>
              </button>
            ))}
          </div>

          <button onClick={handleCheckout} disabled={submitting} className="btn-primary w-full text-lg" data-testid="checkout-button">
            {submitting ? <Loader2 className="animate-spin inline mr-2" size={18}/> : null}
            {user ? `Confirmar — ${total}€` : "Entrar para comprar"}
          </button>
        </div>

        <div className="glass p-8">
          <h2 className="font-display text-3xl mb-4">Como funciona</h2>
          <ol className="space-y-3 text-[color:var(--text-2)] text-sm leading-relaxed list-decimal pl-5">
            <li>Confirmas o pedido com o método escolhido.</li>
            <li>Receberás instruções para enviar o pagamento.</li>
            <li>Um administrador aprova o teu pagamento manualmente.</li>
            <li>Aparecem 33 cartas viradas para baixo. Escolhes <span className="text-gold">{qty}</span>.</li>
            <li>Cartas viram-se: revelam raridade e se calhou prémio.</li>
            <li>Para cada prémio gira-se a roleta: 5€, 10€, 20€ ou 50€.</li>
            <li>Trocas o voucher por prémios reais na <span className="text-gold">loja</span>.</li>
          </ol>

          {orders.length > 0 && (
            <>
              <div className="border-t border-[color:var(--border)] my-6"/>
              <h3 className="font-display text-2xl mb-3">Os teus pedidos</h3>
              <div className="space-y-2 max-h-60 overflow-auto pr-1">
                {orders.slice(0, 5).map(o => (
                  <button key={o.order_id} onClick={() => navigate(`/pagamento/${o.order_id}`)}
                    className="w-full text-left p-3 rounded-md border border-[color:var(--border)] hover:border-gold/40 transition-colors"
                    data-testid={`order-row-${o.order_id}`}>
                    <div className="flex justify-between text-sm">
                      <span>{o.quantity}× cartas · {o.total_eur.toFixed(2)}€</span>
                      <span className={`text-xs uppercase ${o.status==="paid"?"text-gold":o.status==="pending"?"text-[color:var(--text-2)]":"text-red-400"}`}>{o.status}</span>
                    </div>
                    <div className="text-[color:var(--text-3)] text-xs">{new Date(o.created_at).toLocaleString("pt-PT")}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
